import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import PaxtonLogo from '../../assets/paxton-logo.svg';
import LogoutIcon from '../../assets/icons/Lefthand navigation icons/Logout icon.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/Text field icons/Close icon.svg';
import { useLogout } from '../appUIFramework/hooks/useLogout';
import { AppNavigationMenuLink } from './AppNavigationMenuLink';
import { AccountStatusUIState, useAccountInfo } from '../appUIFramework/appBackend/useAccountInfo';
import { useAppPermissions } from '../appUIFramework/appPermisions/useAppPermissions';
import {
  CanNavigateToAdminPermissions,
  CanNavigateToHomePermissions,
  CanNavigateToPaymentPermissions,
  CanNavigateToSitesPermissions,
} from '../appUIFramework/appPermisions/permissionsGroups';
import HomeIcon from '../../assets/icons/Lefthand navigation icons/Home Icon.svg';
import SystemsIcon from '../../assets/icons/Lefthand navigation icons/Systems icon.svg';
import PaymentIcon from '../../assets/icons/Lefthand navigation icons/Payment icon.svg';
import AdminIcon from '../../assets/icons/Lefthand navigation icons/Profile icon.svg';
import { UserPermission } from '../appUIFramework/appPermisions/userPermission';
import callIfEnterKeyPressed from '../jsUtils/callIfEnterKeyPressed';
import { useEntryOrSoloSystemUrl } from './AppRouteSwitch';
import { useFeatureFlags } from '../appUIFramework/featureFlags';

type NavigationLink = {
  to: string,
  icon: any,
  text: string,
  disabled: boolean,
  childRoutes?: string[],
  parents?: string[]
}

export function getAppNavigationLinks(
  accountInfo: any,
  t: (s: string) => string,
  hasAnyOfPermissions: (...permissions: UserPermission[]) => boolean,
  options: { systemRoute: string, systemParent: string, systemTitle: string, paymentRoute: string, paymentParent: string },
) {
  const isAccountOnHold = accountInfo.accountStatus === AccountStatusUIState.Blocked;

  const appNavigationLinks = [] as NavigationLink[];

  if (hasAnyOfPermissions(...CanNavigateToHomePermissions)) {
    appNavigationLinks.push({
      to: '/home',
      icon: HomeIcon,
      text: t('Navigation_Home'),
      disabled: isAccountOnHold,
      childRoutes: ['/profile'],
    });
  }
  if (hasAnyOfPermissions(...CanNavigateToSitesPermissions)) {
    appNavigationLinks.push({
      to: options.systemRoute,
      icon: SystemsIcon,
      text: t(options.systemTitle),
      disabled: isAccountOnHold,
      childRoutes: [],
      parents: [options.systemParent],
    });
  }
  if (hasAnyOfPermissions(...CanNavigateToPaymentPermissions)) {
    appNavigationLinks.push({
      to: options.paymentRoute,
      icon: PaymentIcon,
      text: t('Navigation_Payment'),
      disabled: false,
      childRoutes: ['overview'],
      parents: [options.paymentParent],
    });
  }
  if (hasAnyOfPermissions(...CanNavigateToAdminPermissions)) {
    appNavigationLinks.push({
      to: '/admin',
      icon: AdminIcon,
      text: t('Navigation_Admin'),
      disabled: isAccountOnHold,
      childRoutes: ['people'],
    });
  }
  return appNavigationLinks;
}

export function useAppNavigationLinks() {
  const { hasAnyOfPermissions } = useAppPermissions();
  const { t, i18n } = useTranslation();
  const { accountInfo } = useAccountInfo();
  const { soloUi } = useFeatureFlags();

  const initialAppNavigationLinks = useRef(null as unknown as NavigationLink[]);
  const systemUrl = useEntryOrSoloSystemUrl('/systems');

  const getAppLinksOptions = {
    systemRoute: systemUrl,
    systemParent: '/systems',
    systemTitle: soloUi ? 'SoloSystems' : 'Navigation_Sites',
    paymentRoute: soloUi ? '/payment/solo/overview' : '/payment',
    paymentParent: '/payment',
  };

  if (!initialAppNavigationLinks.current) {
    initialAppNavigationLinks.current = getAppNavigationLinks(accountInfo, t, hasAnyOfPermissions, getAppLinksOptions);
  }

  const [appNavigationLinks, setAppNavigationLinks] = useState(initialAppNavigationLinks.current);

  useEffect(() => {
    setAppNavigationLinks(getAppNavigationLinks(accountInfo, t, hasAnyOfPermissions, getAppLinksOptions));
  }, [i18n.language]);

  return appNavigationLinks;
}

export default function AppNavigationMenu() {
  const { t } = useTranslation();

  const appNavigationLinks = useAppNavigationLinks();
  const handleLogout = useLogout();

  return (
    <div className="app-navigation-menu-container">
      <div className="app-navigation-close-wrapper">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label
          htmlFor="menu-visibility-handler-checkbox"
          className="app-navigation-close"
        >
          <CloseIcon />
        </label>
      </div>
      <div className="app-navigation-logo">
        <img
          src={PaxtonLogo}
          alt="Paxton Logo"
          style={{ maxWidth: '100%' }}
        />
      </div>
      <div className="app-navigation-links">
        {appNavigationLinks.map((link) => (
          <AppNavigationMenuLink
            key={link.to}
            to={link.to}
            childRoutes={link.childRoutes || ['/profile']}
            disabled={link.disabled}
            parents={link.parents}
          >
            <img
              src={link.icon}
              alt={link.text}
            />
            <span>{link.text}</span>
          </AppNavigationMenuLink>
        ))}
      </div>
      <div className="app-navigation-logout">
        <div
          role="button"
          tabIndex={0}
          className="app-mp-bold-16 app-px-30 app-py-12 app-navigation-link app-navigation-logout"
          onClick={handleLogout}
          onKeyDown={callIfEnterKeyPressed(handleLogout)}
        >
          <img
            src={LogoutIcon}
            alt="Logout"
          />
          <span>{t('Sites_Logout')}</span>
        </div>
      </div>
    </div>
  );
}
